import React, { ReactNode, FC, useRef, useEffect, useState } from 'react';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import translate from '@translate';
import appLogo from 'src/utils/appLogo';
import { styled } from '@mui/system';
import StyledLink from '../../../components/StyledLink';
import {
    DashboardIcon,
    TaskIcon,
    TaskActiveIcon,
    OrderIcon,
    ReturnIcon,
    ClaimIcon,
    ReceivingIcon,
    InventoryIcon,
    DocumentIcon,
    TransportIcon,
    SettingsIcon,
    AdminIcon,
    UsersIcon,
    AdminActiveIcon,
    ClaimActiveIcon,
    ReturnActiveIcon,
    ReceivingActiveIcon,
    DocumentActiveIcon,
    SettingsActiveIcon,
    CommandsActiveIcon, IntegrationIcon, IntegrationActiveIcon
} from 'src/components/SkladonIcon/Icons';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { useCheckPermission } from 'src/utils/checkPermission';
import useWindowSize from 'src/utils/useWindowSize';
import { CommandsIcon } from 'src/components/SkladonIcon/SmallIcons';

type NavigationItemType = {
    label?: string;
    url?: string;
    icon?: ReactNode;
    iconActive?: ReactNode;
    activeUrl?: string;
    excludedActiveUrls?: string[];
    style?: React.CSSProperties | undefined;
    permission?: string;
    divider?: boolean;
    bottom?: boolean;
};

const Logo = styled('img')({
    width: '190px',
    marginTop: '-12px',
    marginLeft: '1px',
});

const BottomItemsSpacer = styled('div')(({ theme }) => ({
    // [theme.breakpoints.up('sm')]: {
    //     // '@media (max-height: 770px)': {
    //     width: '100%',
    //     height: '10px',
    // },
}));
const BottomItems = styled('div')<{ displayBottom?: boolean }>(({ theme, displayBottom }) => ({
    ...(displayBottom && {
        position: 'absolute',
        bottom: '0',
        width: '100%',
    }),
}));

const NavigationItem: FC<NavigationItemType> = (props) => {
    return (
        <StyledLink
            to={props.url}
            activeUrl={props.activeUrl}
            excludedActiveUrls={props.excludedActiveUrls}
            activeClass
            activeChildren={
                props.iconActive && (
                    <ListItem key={props.url} disablePadding style={props.style}>
                        <ListItemButton>
                            <ListItemIcon>{props.iconActive}</ListItemIcon>
                            <ListItemText primary={props.label} />
                        </ListItemButton>
                    </ListItem>
                )
            }
        >
            <ListItem key={props.url} disablePadding style={props.style} className="without-active-icon">
                <ListItemButton>
                    <ListItemIcon>{props.icon}</ListItemIcon>
                    <ListItemText primary={props.label} />
                </ListItemButton>
            </ListItem>
        </StyledLink>
    );
};

const NAVIGATION_ITEMS: NavigationItemType[] = [
    {
        url: '/overview/',
        label: 'sidebar.overview',
        icon: <DashboardIcon />,
    },
    {
        url: '/task_list/',
        label: 'sidebar.tasks',
        icon: <TaskIcon />,
        iconActive: <TaskActiveIcon />,
        permission: 'permissionTasks',
    },
    { divider: true },
    {
        url: '/orders/all',
        activeUrl: '/orders/',
        label: 'sidebar.orders',
        icon: <OrderIcon />,
        permission: 'permissionOrders',
    },
    {
        url: '/claims/',
        label: 'sidebar.claims',
        icon: <ClaimIcon />,
        iconActive: <ClaimActiveIcon />,
        permission: 'permissionClaims',
    },
    {
        url: '/returns/',
        label: 'sidebar.returns',
        icon: <ReturnIcon />,
        iconActive: <ReturnActiveIcon />,
        permission: 'permissionReturns',
    },
    {
        url: '/receivings/',
        label: 'sidebar.receivings',
        icon: <ReceivingIcon />,
        iconActive: <ReceivingActiveIcon />,
        permission: 'permissionReceivings',
    },
    { divider: true },
    {
        url: '/transports/',
        activeUrl: '/transports/',
        label: 'sidebar.transports',
        icon: <TransportIcon />,
        permission: 'permissionTransports',
    },
    {
        url: '/products/all/',
        activeUrl: '/products/',
        excludedActiveUrls: ['/products/trash/'],
        label: 'sidebar.products',
        icon: <InventoryIcon />,
        permission: 'permissionProducts',
    },
    {
        url: '/products/trash/',
        activeUrl: '/products/trash/',
        label: 'sidebar.productTrash',
        icon: <InventoryIcon />,
        permission: 'permissionProducts',
    },
    { divider: true },
    {
        url: '/documents/',
        label: 'sidebar.documents',
        icon: <DocumentIcon />,
        iconActive: <DocumentActiveIcon />,
    },
    {
        url: '/users/',
        label: 'sidebar.users',
        icon: <UsersIcon />,
        permission: 'permissionUsers',
        bottom: true,
    },
    {
        url: '/commands/product_completion_sheets',
        label: 'sidebar.commands',
        icon: <CommandsIcon />,
        iconActive: <CommandsActiveIcon />,
        permission: 'permissionCommands',
        bottom: true,
    },
    {
        url: '/settings/provider_companies/',
        activeUrl: '/settings/',
        label: 'sidebar.settings',
        icon: <SettingsIcon />,
        iconActive: <SettingsActiveIcon />,
        permission: 'permissionSettings',
        bottom: true,
    },
    {
        url: '/integration/',
        label: 'sidebar.integration',
        icon: <IntegrationIcon />,
        iconActive: <IntegrationActiveIcon />,
        permission: 'permissionIntegration',
        bottom: true,
    },
    {
        url: '/admin/providers/',
        activeUrl: '/admin/',
        label: 'sidebar.admin',
        icon: <AdminIcon />,
        iconActive: <AdminActiveIcon />,
        permission: 'permissionAdmin',
        bottom: true,
    },
];

const NavigationContainer: FC = () => {
    const size = useWindowSize();

    const topElement = useRef<HTMLDivElement>(null);
    const bottomElement = useRef<HTMLDivElement>(null);

    const [topElementheight, setTopElementheight] = useState<number>(0);
    const [bottomElementheight, setBottomElementheight] = useState<number>(0);

    const permissionOrders = useCheckPermission('ORDER.LIST');
    const permissionClaims = useCheckPermission('CLAIM.LIST');
    const permissionReturns = useCheckPermission('RETURN.LIST');
    const permissionReceivings = useCheckPermission('RECEIVING.LIST');
    const permissionTransportList = useCheckPermission('TRANSPORT.LIST');
    const permissionPackageTypesList = useCheckPermission('TRANSPORT_PACKAGE_TYPE.LIST');
    const permissionTransports = permissionTransportList || permissionPackageTypesList;
    const permissionProducts = useCheckPermission('PRODUCT.LIST');
    const permissionUsers = useCheckPermission('USER.LIST');
    const permissionSettings = useCheckPermission('PROVIDER_COMPANIES.LIST') || useCheckPermission('PROVIDER_TRANSFER_COMPANY.LIST');
    const permissionAdmin = useCheckPermission('ADMIN.LIST');
    const permissionTasks = useCheckPermission('TASKS.LIST');
    const permissionProductCompletionSheets = useCheckPermission('PRODUCT_COMPLETION_SHEET.LIST');
    const permissionIntegration = useCheckPermission('INTEGRATION.LIST');
    const permissionCommands = permissionProductCompletionSheets;

    useEffect(() => {
        if (!topElement.current) return; // wait for the elementRef to be available
        const resizeObserver = new ResizeObserver(() => {
            setTopElementheight(topElement.current?.offsetHeight || 0);
            // Do what you want to do when the size of the element changes
        });
        resizeObserver.observe(topElement.current);
        return () => resizeObserver.disconnect(); // clean up
    }, []);

    useEffect(() => {
        if (!bottomElement.current) return; // wait for the elementRef to be available
        const resizeObserver = new ResizeObserver(() => {
            setBottomElementheight(bottomElement.current?.offsetHeight || 0);
            // Do what you want to do when the size of the element changes
        });
        resizeObserver.observe(bottomElement.current);
        return () => resizeObserver.disconnect(); // clean up
    }, []);

    const hiddenPermisions: { [key: string]: boolean } = {
        permissionOrders,
        permissionClaims,
        permissionReturns,
        permissionReceivings,
        permissionTransports,
        permissionProducts,
        permissionUsers,
        permissionSettings,
        permissionAdmin,
        permissionTasks,
        permissionCommands,
        permissionIntegration
    };

    let topItems = NAVIGATION_ITEMS.filter(
        (item) => !item.bottom && (!item.permission || hiddenPermisions[item.permission])
    );
    topItems = topItems.filter((item, key) => !(item.divider && (!key || topItems[key - 1].divider)));
    topItems = topItems.map((item) => {
        if (item.url === '/transports/' && !permissionTransportList && permissionPackageTypesList) {
            return { ...item, url: '/transports/package-types/' };
        }
        return item;
    });

    const bottomItems = NAVIGATION_ITEMS.filter(
        (item) => item.bottom && (!item.permission || hiddenPermisions[item.permission])
    );

    const allItemsTogether = (size.height || 0) - (topElementheight + bottomElementheight + 80) > 0;

    return (
        <div style={{ height: '100%' }}>
            <Toolbar>
                <StyledLink to="/overview/" style={{ display: 'flex' }}>
                    <Logo src={appLogo} alt="skladon background" />
                </StyledLink>
            </Toolbar>
            <List style={{ height: 'calc(100% - 80px)' }}>
                <div ref={topElement}>
                    {topItems.map((item, key) => {
                        if (item.divider) {
                            return <Divider key={key} />;
                        } else {
                            return (
                                <NavigationItem
                                    key={key}
                                    url={item.url}
                                    activeUrl={item.activeUrl}
                                    excludedActiveUrls={item.excludedActiveUrls}
                                    label={translate(item.label || '')}
                                    icon={item.icon}
                                    iconActive={item.iconActive}
                                />
                            );
                        }
                    })}
                </div>
                <BottomItemsSpacer />
                <BottomItems ref={bottomElement} displayBottom={!!allItemsTogether}>
                    {bottomItems.map((item, key) => {
                        if (item.divider) {
                            return <Divider key={key} />;
                        } else {
                            return (
                                <NavigationItem
                                    key={key}
                                    url={item.url}
                                    activeUrl={item.activeUrl}
                                    excludedActiveUrls={item.excludedActiveUrls}
                                    label={translate(item.label || '')}
                                    icon={item.icon}
                                    iconActive={item.iconActive}
                                />
                            );
                        }
                    })}
                </BottomItems>
            </List>
        </div>
    );
};

export default NavigationContainer;
